import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Box, Button, Flex, Icon, Text, useColorModeValue } from "@chakra-ui/react";
import { MdVisibility, MdVisibilityOff, MdLock } from "react-icons/md";
import { useWith2FA } from "../../../tools/twoFactor";

/**
 * Reveals the OCR text via the 2FA-protected endpoint (audited as ACCESS_OCR).
 * @param {number} certId - The certification id
 * @param {Function} runWith2FA - The 2FA-aware request runner
 * @param {Function} setText - Setter for the revealed text
 * @param {Function} setLoading - Setter for the loading flag
 * @returns {Promise<void>}
 */
async function revealOcr(certId, runWith2FA, setText, setLoading) {
    setLoading(true);
    try {
        const { data } = await runWith2FA({ method: "get", url: `/admin/certifications/${certId}/ocr_reveal` });
        setText(data?.text_extracted ?? "");
    } catch (error) {
        if (error?.message !== "2fa_cancelled") toast.error("Révélation du texte OCR impossible");
    } finally {
        setLoading(false);
    }
}

/**
 * Placeholder shown while the OCR text is hidden, with a reveal button.
 * @param {Object} props
 * @param {boolean} props.loading - Whether a reveal request is in progress
 * @param {Function} props.onReveal - Reveal handler
 * @returns {React.ReactElement}
 */
function OcrHiddenState({ loading, onReveal }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const noticeColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box p="16px" bg={bg} borderRadius="12px">
            <Flex align="center" gap="8px" mb="12px">
                <Icon as={MdLock} color={noticeColor} w="18px" h="18px" />
                <Text color={noticeColor} fontSize="sm">
                    Texte masqué — donnée d'identité sensible (RGPD Art. 9). La révélation est tracée.
                </Text>
            </Flex>
            <Button onClick={onReveal} isLoading={loading} loadingText="Vérification..."
                leftIcon={<Icon as={MdVisibility} />} colorScheme="brand" variant="outline" size="sm">
                Révéler le texte
            </Button>
        </Box>
    );
}

/**
 * Renders the revealed OCR text with a button to re-mask it.
 * @param {Object} props
 * @param {string} props.text - The revealed OCR text
 * @param {Function} props.onHide - Re-mask handler
 * @returns {React.ReactElement}
 */
function OcrRevealedState({ text, onHide }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const textColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Box p="16px" bg={bg} borderRadius="12px">
            <Flex justify="flex-end" mb="8px">
                <Button onClick={onHide} leftIcon={<Icon as={MdVisibilityOff} />} variant="ghost" size="sm">
                    Masquer
                </Button>
            </Flex>
            <Text color={textColor} fontSize="sm" lineHeight="1.6" whiteSpace="pre-wrap">
                {text || "Aucun texte extrait."}
            </Text>
        </Box>
    );
}

/**
 * OCR text hidden by default with a 2FA-gated reveal. Re-masks when the
 * certification changes or the component unmounts (tab switch).
 * @param {Object} props
 * @param {number} props.certId - The certification id
 * @returns {React.ReactElement}
 */
function SensitiveOcrText({ certId }) {
    const runWith2FA = useWith2FA();
    const [text, setText] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => { setText(null); }, [certId]);

    if (text === null) {
        return <OcrHiddenState loading={loading} onReveal={() => revealOcr(certId, runWith2FA, setText, setLoading)} />;
    }
    return <OcrRevealedState text={text} onHide={() => setText(null)} />;
}

export default SensitiveOcrText;
